import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { i3Assets } from '../utils/i3Assets';
import { useRevealSections } from '../hooks/useReveal';
import '../styles/I3Page.css';
import '../styles/I3About.css';

const ROLES = ['coaches', 'founders', 'ministry'];
const FOCUS = [
  'Marriage enrichment',
  'Leadership development',
  'Financial wellness',
];
const CONVICTIONS = [
  { title: 'Integrity', line: 'We lead with honesty and clarity.' },
  { title: 'Intentional growth', line: 'Small steps, lasting change.' },
  { title: 'Impact', line: 'Seeing people walk in purpose and live healed lives.' },
];

const I3About: React.FC = () => {
  const wrapRef = useRef<HTMLDivElement>(null);
  useRevealSections(wrapRef);

  return (
    <article className="i3-about">
      <h1 className="i3-about-sr-only">About</h1>
      <div className="i3-page-wrap" ref={wrapRef}>
        {/* Jason Bergh–inspired: pictures left, words right — warm dark editorial block */}
        <section className="i3-about-editorial i3-reveal" aria-label="About Troy and Sharrea">
          <div className="i3-about-editorial-inner">
            <div className="i3-about-editorial-photos">
              <div className="i3-about-editorial-main">
                <img
                  src={i3Assets.photos.himHer}
                  alt="Troy and Sharrea"
                  className="i3-about-editorial-img"
                  onError={(e) => {
                    const el = e.currentTarget;
                    const step = el.dataset.i3Fallback || '0';
                    if (step === '0') {
                      el.dataset.i3Fallback = '1';
                      el.src = i3Assets.photos.funPic1;
                    } else if (step === '1') {
                      el.dataset.i3Fallback = '2';
                      el.src = i3Assets.photos.thisIsUs;
                    }
                  }}
                />
              </div>
            </div>
            <div className="i3-about-editorial-copy">
              <span className="i3-about-label">about us</span>
              <h2 className="i3-about-title">Troy & Sharrea</h2>
              <div className="i3-about-pills">
                {ROLES.map((r) => (
                  <span key={r} className="i3-about-pill">{r}</span>
                ))}
              </div>
              <p className="i3-about-credentials">
                I-3 Coaching & Consulting — Founders
              </p>
              <p className="i3-about-narrative-lead">
                Our love journey began in 2022. In 2025 we founded I-3 Coaching & Consulting with a shared mission: to see men and women walk in purpose and live healed lives. We bring decades of leadership, ministry, and life experience to the couples, individuals, and leaders we serve.
              </p>
              <button type="button" className="i3-about-read-prompt" onClick={() => document.getElementById('i3-about-narrative')?.scrollIntoView({ behavior: 'smooth' })}>
                read the text
              </button>
            </div>
          </div>

          <div id="i3-about-narrative" className="i3-about-narrative i3-about-narrative--stacked">
            <section className="i3-about-solo-section" aria-label="Troy and Sharrea portraits">
              <div className="i3-about-solo-section-inner">
                <div className="i3-about-solo-portraits i3-founder-bios-photos">
                  <div className="i3-founder-bio-photo">
                    <img
                      src={i3Assets.photos.troySolo}
                      alt="Troy"
                      className="i3-about-editorial-img"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  </div>
                  <div className="i3-founder-bio-photo">
                    <img
                      src={i3Assets.photos.sharreaSolo}
                      alt="Sharrea"
                      className="i3-about-editorial-img"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  </div>
                </div>
              </div>
            </section>

            <div className="i3-about-narrative-continued">
              <div className="i3-founder-bios" aria-label="Troy and Sharrea bios">
                  <div className="i3-founder-bios-divider" aria-hidden />
                  <div className="i3-founder-bios-text">
                    <div className="i3-founder-bio-block">
                      <div className="i3-founder-bio">
                        <p>
                          <strong>Troy</strong> brings more than 30 years of pastoral leadership experience, including 16 years as a Senior Pastor. His work spans both ministry and education, where he is deeply committed to developing leaders and strengthening organizational effectiveness.
                        </p>
                        <p>
                          He currently serves as the Associate Pastor of Ministries and Discipleship at his home church, providing strategic guidance, spiritual formation, and leadership development. In his secular role, Troy functions as a Lead Instructional Coach, supporting educators and advancing best practices in teaching and learning.
                        </p>
                        <p>
                          With a unique blend of pastoral insight and educational expertise, Troy remains dedicated to equipping individuals and teams to grow, lead, and thrive.
                        </p>
                      </div>
                    </div>
                    <div className="i3-founder-bio-block">
                      <div className="i3-founder-bio">
                        <p>
                          <strong>Sharrea</strong> brings more than 20 years of leadership experience across ministry and corporate environments. She is a trained facilitator for multiple financial wellness ministries, where she equips individuals and families with practical tools for stewardship and long-term financial stability.
                        </p>
                        <p>
                          In addition to her work in the community, Sharrea serves as the Business Manager and a Trustee at her home church, providing strategic oversight and operational leadership. She also excels in her secular role as a Senior Financial Business Analyst, where she leverages her expertise to support organizational efficiency and informed decision making.
                        </p>
                        <p>
                          Her commitment to service, financial empowerment, and organizational excellence continues to shape the impact she makes in every space she leads.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </section>

        <section className="i3-section i3-about-focus i3-reveal">
        <h2 className="i3-about-section-title">What we focus on</h2>
        <ul className="i3-about-numbered-grid">
          {FOCUS.map((item, i) => (
            <li key={item} className="i3-about-numbered-item">
              <span className="i3-about-num">{String(i + 1).padStart(2, '0')}.</span>
              <span className="i3-about-numbered-text">{item}</span>
            </li>
          ))}
        </ul>
        </section>

        <section className="i3-section i3-section-alt i3-about-convictions i3-reveal">
        <h2 className="i3-about-section-title">Our convictions</h2>
        <div className="i3-about-blocks">
          {CONVICTIONS.map(({ title, line }) => (
            <div key={title} className="i3-about-block">
              <span className="i3-about-block-title">{title}</span>
              <p className="i3-about-block-line">{line}</p>
            </div>
          ))}
        </div>
        </section>

        <section className="i3-section i3-about-vision-mission i3-reveal">
        <blockquote className="i3-about-vision">
          To cultivate thriving families, empowered leaders, and financially confident communities by inspiring personal growth and intentional transformation.
        </blockquote>
        <p className="i3-about-mission">
          We equip individuals, couples, and leaders with tools, coaching, and education to strengthen relationships, elevate leadership capacity, and build lasting financial wellness.
        </p>
        </section>

        <section className="i3-section i3-about-cta i3-anchor-dark i3-reveal">
          <h2>Let&apos;s connect</h2>
          <p>Ready to start a conversation? We&apos;d love to hear from you.</p>
          <Link to="/contact" className="i3-cta i3-cta-primary">
            Get in touch
          </Link>
        </section>
      </div>
    </article>
  );
};

export default I3About;
